"use client";

import { cn } from "@/lib/utils/general-utils";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

export interface ModernPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (_page: number) => void;
  onItemsPerPageChange?: (_itemsPerPage: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  loading?: boolean;
  className?: string;
}

/** Avoid rendering huge native <select> lists for very large page counts. */
const MAX_PAGE_PICKER_OPTIONS = 300;

function pagePickerOptions(currentPage: number, totalPages: number): number[] {
  const capped = Math.max(
    1,
    Math.min(Math.max(totalPages, 1), MAX_PAGE_PICKER_OPTIONS),
  );
  const pages = Array.from({ length: capped }, (_, index) => index + 1);
  if (currentPage > capped && currentPage <= Math.max(totalPages, 1)) {
    pages.push(currentPage);
  }
  return pages;
}

function PageNumberControl({
  currentPage,
  totalPages,
  disabled,
  onPageChange,
}: Readonly<{
  currentPage: number;
  totalPages: number;
  disabled: boolean;
  onPageChange: (page: number) => void;
}>) {
  const pages = pagePickerOptions(currentPage, totalPages);
  const digitCount = String(Math.max(totalPages, 1)).length;
  const selectedPage = pages.includes(currentPage)
    ? currentPage
    : (pages[0] ?? 1);

  return (
    <span className="relative inline-flex items-center">
      <select
        aria-label="Page number"
        disabled={disabled}
        value={selectedPage}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (!disabled && next !== currentPage) {
            onPageChange(next);
          }
        }}
        className={cn(
          "box-border h-7 appearance-none cursor-pointer pl-2 pr-6 text-xs font-medium tabular-nums text-center text-gray-900",
          "border border-gray-200 rounded-md bg-white",
          "focus:outline-none focus:ring-1 focus:ring-gray-300",
          "disabled:cursor-not-allowed disabled:opacity-60",
        )}
        style={{
          minWidth: "4.5rem",
          width: `${Math.max(digitCount, 3) + 4.5}ch`,
        }}
      >
        {pages.map((page) => (
          <option key={page} value={page}>
            {page}
          </option>
        ))}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute right-1.5 size-3.5 text-text-tertiary"
      />
    </span>
  );
}

export function ModernPagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  pageSizeOptions = [10, 25, 50, 100],
  showPageSizeSelector = true,
  loading = false,
  className = "",
}: Readonly<ModernPaginationProps>) {
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-text-tertiary",
        className,
      )}
    >
      <button
        type="button"
        disabled={!canGoPrevious || loading}
        onClick={() =>
          canGoPrevious && !loading && onPageChange(currentPage - 1)
        }
        className="inline-flex shrink-0 items-center gap-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="size-4" />
        Previous
      </button>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <span className="inline-flex items-center gap-1">
          <span>Page</span>
          <PageNumberControl
            currentPage={currentPage}
            totalPages={totalPages}
            disabled={loading}
            onPageChange={onPageChange}
          />
          <span>
            of <span className="font-medium text-gray-900">{totalPages}</span>
            {totalItems > 0 && (
              <span className="hidden sm:inline">
                {" "}
                ({totalItems.toLocaleString()} total)
              </span>
            )}
          </span>
        </span>

        {showPageSizeSelector && onItemsPerPageChange && (
          <select
            value={itemsPerPage}
            onChange={(e) =>
              !loading && onItemsPerPageChange(Number(e.target.value))
            }
            disabled={loading}
            className="hidden sm:inline h-7 px-1.5 pr-6 text-xs border border-gray-200 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Items per page"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </select>
        )}
      </div>

      <button
        type="button"
        disabled={!canGoNext || loading}
        onClick={() => canGoNext && !loading && onPageChange(currentPage + 1)}
        className="inline-flex shrink-0 items-center gap-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Next
        <ChevronRight className="size-4" />
      </button>
    </div>
  );
}
